import React, { Suspense, lazy, useState, useRef, useEffect } from "react";
import { scrollToSection, scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton
} from "@mui/material";
import {
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Event as EventIcon,
  Group as GroupIcon,
  MusicNote as MusicIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

// Assets
import heroDesktop from "../assets/dhol-players-uk-wedding-hero.webp";
import heroMobile from "../assets/dhol-players-uk-wedding-hero-mobile.webp";
import imperial1 from "../assets/dhol-drummers-aladdin-show-performance.webp";
import imperial2 from "../assets/dhol-players-disneyland-paris-performance.webp";
import imperial4 from "../assets/dhol-players-asian-wedding-reception.webp";
import imperial5 from "../assets/dhol-drummers-meta-corporate-event.webp";
import imperial6 from "../assets/dhol-drummers-essex-private-party.webp";
import imperial7 from "../assets/dhol-players-bbc-asian-network-performance.webp";
import imperial8 from "../assets/dhol-players-meridian-grand-anniversary-event.webp";
import luxuryOutfits from "../assets/dhol-drummers-luxury-wedding-outfits.webp";

// World locations
import denmark from "../assets/dhol-players-denmark-wedding.webp";
import london from "../assets/dhol-players-london-natural-history-museum.webp";
import losAngeles from "../assets/dhol-players-los-angeles-wedding.webp";
import italy from "../assets/dhol-players-venice-italy-destination-wedding.webp";
import monaco from "../assets/dhol-players-monaco-event.webp";
import philippines from "../assets/dhol-players-philippines-event.webp";
import cancun from "../assets/dhol-players-cancun-destination-wedding.webp";
import southAfrica from "../assets/dhol-players-south-africa-wedding.webp";
import amsterdam from "../assets/dhol-players-amsterdam-event.webp";
import jaipur from "../assets/dhol-players-jaipur-india-wedding.webp";
import malta from "../assets/dhol-players-malta-destination-wedding.webp";
import mexico from "../assets/dhol-players-mexico-destination-wedding.webp";
import texas from "../assets/dhol-players-texas-wedding.webp";

// Animation
import { motion } from "framer-motion";

// Components - Regular imports
import { useLocation, Link } from "react-router-dom";
import Navbar from "../components/navbar";
import BookingForm from "../components/bookingForm";
import Footer from "../components/footer";

// Components - Code-split heavy components
const LiquidEther = lazy(() => import("../components/LiquidEther"));
const LightRays = lazy(() => import("../components/LightRays"));
const AnimatedCounter = lazy(() => import("../components/AnimatedCounter"));
const InfiniteMenuFull = lazy(() => import("../components/InfiniteMenuFull"));

const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');

// Hook that returns false during pre-rendering AND initial hydration (matching
// the pre-rendered HTML), then true after mount. This prevents hydration
// mismatches for heavy components excluded from the react-snap snapshot.
const useIsClient = () => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    if (!isPrerendering) setIsClient(true);
  }, []);
  return isClient;
};

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Handle scroll-to-section when navigating from another page
  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (sectionId) {
      if (sectionId === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        scrollToSection(sectionId);
      }
    }
  }, [location.state]);

  // World locations data
  const worldLocations = [
    {
      image: london,
      title: "London",
    },
    {
      image: denmark,
      title: "Copenhagen",
    },
    {
      image: losAngeles,
      title: "Los Angeles",
    },
    {
      image: texas,
      title: "Texas",
    },
    {
      image: italy,
      title: "Venice",
    },
    {
      image: monaco,
      title: "Monaco",
    },
    {
      image: philippines,
      title: "Phillipines",
    },
    {
      image: cancun,
      title: "Cancun",
    },
    {
      image: southAfrica,
      title: "South Africa",
    },
    {
      image: amsterdam,
      title: "Amsterdam",
    },
    {
      image: jaipur,
      title: "Jaipur",
    },
    {
      image: malta,
      title: "Malta",
    },
    {
      image: mexico,
      title: "Mexico",
    },
  ];

  return (
    <>
      <Navbar />
      <Box component="main">
        {/* Hero Section */}
        <HeroSection isMobile={isMobile} scrollToBooking={scrollToBooking} />

        {/* Services Section */}
        <ServicesSection />

        {/* About Imperial Drummers */}
        <AboutSection isMobile={isMobile} worldLocations={worldLocations} />

        {/* Imperial Around the World */}
        {/* <Box id="world-section" sx={{
          py: 8,
          backgroundColor: '#0a0015',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" gutterBottom sx={{ color: '#ffd700', mb: 2 }}>
              Imperial Around the World
            </Typography>
            <Typography variant="h6" align="center" sx={{ mb: 4, color: 'rgba(255, 255, 255, 0.8)', maxWidth: '700px', mx: 'auto' }}>
              Celebrating across 30+ countries with local expertise and authentic performances
            </Typography>
          </Container>
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <InfiniteMenuFull items={worldLocations} />
          </Box>
        </Box> */}

        {/* Portfolio/Gallery */}
        <PortfolioSection />

        {/* Luxury Showcase */}
        <LuxuryShowcaseSection />

        {/* Testimonials */}
        {/* <TestimonialsSection /> */}

        {/* FAQ */}
        <FAQSection />

        {/* Booking Form */}
        <Box sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

// Hero Section Component
const HeroSection = ({ isMobile, scrollToBooking }) => (
  <Box sx={{
    position: 'relative',
    height: { xs: '80vh', md: '85vh' }, // Increased height for more drama
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    backgroundImage: {
      xs: `url(${heroMobile})`,
      md: `url(${heroDesktop})`
    },
    backgroundSize: 'cover',
    backgroundPosition: { xs: 'center 28%', md: 'center 38%' },
    // Add a parallax-like feel with background-attachment if supported
    backgroundAttachment: { md: 'fixed' }
  }}>
    {/* Sophisticated Overlay System */}
    <Box sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: {
        // Mobile: stronger top-to-bottom scrim so the busy photo recedes and text stays legible
        xs: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.78) 100%)',
        // Desktop: left-to-right gradient keeps the left-aligned copy readable
        md: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)'
      },
      zIndex: 1
    }} />

    {/* Vignette effect for focus */}
    <Box sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      boxShadow: 'inset 0 0 150px rgba(0,0,0,0.5)',
      zIndex: 1,
      pointerEvents: 'none'
    }} />

    <Container maxWidth="lg" sx={{
      textAlign: { xs: 'center', md: 'left' },
      zIndex: 2,
      position: 'relative',
      px: { xs: 2, sm: 4 },
      pt: { xs: '60px', md: '80px' }
    }}>
      <Box sx={{ maxWidth: { md: '750px' } }}>
        <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0s' }}>
          <Typography
            variant="overline"
            sx={{
              color: '#ffd700',
              fontWeight: 700,
              letterSpacing: '3px',
              mb: 1,
              display: 'block',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '0.75rem', md: '0.9rem' }
            }}
          >
            ESTABLISHED SINCE 2014
          </Typography>
        </Box>

        <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.1s' }}>
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              fontWeight: 800,
              mb: 2,
              textShadow: '0 4px 12px rgba(0,0,0,0.5)',
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              lineHeight: 1.1,
              fontFamily: '"EB Garamond SC", serif',
            }}
          >
            International <br />
            <span style={{ color: '#ffd700' }}>Drumming</span> Collective
          </Typography>
        </Box>

        <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.2s' }}>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.25rem' },
              lineHeight: 1.6,
              maxWidth: '600px',
              fontWeight: 400,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Elevate your celebration with the UK's finest Punjabi Dhol Players.
            Delivering authentic energy and premium entertainment across 30+ countries.
          </Typography>
        </Box>

        <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.3s' }}>
          <Grid container spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }}>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                size="large"
                onClick={scrollToBooking}
                startIcon={<EventIcon />}
                color="gold"
                sx={{
                  px: 5,
                  py: 2,
                  boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)',
                  '&:hover': {
                    boxShadow: '0 12px 25px rgba(255, 215, 0, 0.4)',
                  }
                }}
              >
                Book Your Event
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/services"
                sx={{
                  px: 5,
                  py: 2,
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                View Services
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>

  </Box>
);

// Services Section Component
const ServicesSection = () => {
  const isClient = useIsClient();
  const videoRef1 = useRef(null);
  const videoRef2 = useRef(null);
  const videoRef3 = useRef(null);

  const [showPlayIcon1, setShowPlayIcon1] = useState(true);
  const [showPlayIcon2, setShowPlayIcon2] = useState(true);
  const [showPlayIcon3, setShowPlayIcon3] = useState(true);

  const muteAll = () => {
    [videoRef1, videoRef2, videoRef3].forEach(ref => { if (ref.current) ref.current.muted = true; });
    setShowPlayIcon1(true);
    setShowPlayIcon2(true);
    setShowPlayIcon3(true);
  };

  const handleVideoClick1 = () => {
    if (!videoRef1.current) return;
    const unmuting = videoRef1.current.muted;
    muteAll();
    if (unmuting) { videoRef1.current.muted = false; setShowPlayIcon1(false); }
  };

  const handleVideoClick2 = () => {
    if (!videoRef2.current) return;
    const unmuting = videoRef2.current.muted;
    muteAll();
    if (unmuting) { videoRef2.current.muted = false; setShowPlayIcon2(false); }
  };

  const handleVideoClick3 = () => {
    if (!videoRef3.current) return;
    const unmuting = videoRef3.current.muted;
    muteAll();
    if (unmuting) { videoRef3.current.muted = false; setShowPlayIcon3(false); }
  };

  return (
    <Box id="services-section" sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" gutterBottom sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
          Our Highlights
        </Typography>
        <Typography variant="h6" align="center" sx={{ mb: 6, color: '#666', maxWidth: '700px', mx: 'auto', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
          Professional dhol entertainment tailored to your celebration, available worldwide with local expertise
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.3s' } }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 200,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={handleVideoClick2}
              >
                {isClient && (
                  <Box
                    component="video"
                    ref={videoRef2}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  >
                    <source src={process.env.REACT_APP_CORPORATE_EVENTS_VIDEO} type="video/mp4" />
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.3s',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  {showPlayIcon2 ? <VolumeOffIcon sx={{ fontSize: 22, color: 'white' }} /> : <VolumeUpIcon sx={{ fontSize: 22, color: 'white' }} />}
                </Box>
              </Box>
              <CardContent>
                <Typography gutterBottom variant="h5" component="h3" align="center" sx={{ color: '#5C005D' }}>
                  Fairmont Windsor Park
                </Typography>
                {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                </Typography> */}
                {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Global Corporate Events" size="small" />
                  <Chip label="International Launches" size="small" />
                  <Chip label="Cultural Exhibitions" size="small" />
                </Box> */}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.3s' } }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 200,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={handleVideoClick1}
              >
                {isClient && (
                  <Box
                    component="video"
                    ref={videoRef1}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  >
                    <source src={process.env.REACT_APP_WEDDING_PACKAGE_VIDEO} type="video/mp4" />
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.3s',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  {showPlayIcon1 ? <VolumeOffIcon sx={{ fontSize: 22, color: 'white' }} /> : <VolumeUpIcon sx={{ fontSize: 22, color: 'white' }} />}
                </Box>
              </Box>
              <CardContent>
                <Typography gutterBottom variant="h5" component="h3" align="center" sx={{ color: '#5C005D' }}>
                  DJ VIX x Imperial x MC Bushkin
                </Typography>
                {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                </Typography> */}
                {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Destination Weddings" size="small" />
                  <Chip label="International Baraat" size="small" />
                  <Chip label="Multi-Day Events" size="small" />
                </Box> */}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.3s' } }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 200,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={handleVideoClick3}
              >
                {isClient && (
                  <Box
                    component="video"
                    ref={videoRef3}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  >
                    <source src={process.env.REACT_APP_FESTIVAL_CELEBRATIONS_VIDEO} type="video/mp4" />
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'opacity 0.3s',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  {showPlayIcon3 ? <VolumeOffIcon sx={{ fontSize: 22, color: 'white' }} /> : <VolumeUpIcon sx={{ fontSize: 22, color: 'white' }} />}
                </Box>
              </Box>
              <CardContent>
                <Typography gutterBottom variant="h5" component="h3" align="center" sx={{ color: '#5C005D' }}>
                  Natural History Museum
                </Typography>
                {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Authentic performances for a wedding at the Natural History Museums.
                </Typography> */}
                {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Weddings" size="small" />
                  <Chip label="Corporate Events" size="small" />
                  <Chip label="Cultural Exhibitions" size="small" />
                </Box> */}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// About Section Component
const AboutSection = ({ isMobile, worldLocations }) => {
  const isClient = useIsClient();
  return (
    <Box id="about-section" sx={{
      position: 'relative',
      py: 8,
      backgroundColor: '#1a0a2e',
      overflow: 'hidden'
    }}>
      {/* Liquid Ether Background */}
      {isClient && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}>
          <Suspense fallback={<div />}>
            <LiquidEther
              colors={['#8017b5', '#8f4d8c', '#a12b9f']}
              mouseForce={20}
              cursorSize={100}
              isViscous={false}
              viscous={50}
              iterationsViscous={32}
              iterationsPoisson={32}
              resolution={0.5}
              isBounce={false}
              autoDemo={true}
              autoSpeed={0.8}
              autoIntensity={2.2}
              takeoverDuration={0.25}
              autoResumeDelay={3000}
              autoRampDuration={0.6}
            />
          </Suspense>
        </div>
      )}

      {/* Dark overlay for text contrast */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 1
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom sx={{ color: 'white', fontSize: { xs: '2.5rem', md: '3.75rem' }, textAlign: { xs: 'center', md: 'left' } }}>
              About Imperial Drummers
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 3, fontSize: { xs: '1.125rem', md: '1.25rem' }, textAlign: { xs: 'center', md: 'left' } }}>
              UK-based dhol artists bringing authentic Indian rhythms to 30+ countries worldwide
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.85)', textAlign: { xs: 'center', md: 'left' } }}>
              Imperial Drummers is a UK-based collective of professional musicians specialising in traditional
              Punjabi dhol and modern Bollywood entertainment. With over 15 years of experience, we've
              travelled from the UK to perform at thousands of weddings and cultural celebrations across Europe, North America, Asia, and beyond.
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.85)', textAlign: { xs: 'center', md: 'left' } }}>
              Our team combines authentic traditional techniques with contemporary performance styles,
              ensuring your celebration is both culturally meaningful and thoroughly entertaining, whether in the UK or abroad.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Suspense fallback={<Box sx={{ width: '100px', height: '48px' }} />}>
                      <AnimatedCounter
                        target={2000}
                        fontSize={isMobile ? 24 : 34}
                        padding={0}
                        places={[1000, 100, 10, 1]}
                        gap={2}
                        textColor="#ffd700"
                        fontWeight="bold"
                        gradientFrom="transparent"
                        gradientTo="transparent"
                      />
                    </Suspense>
                    <Typography variant="h4" sx={{ color: '#ffd700', fontWeight: 'bold', ml: 0.5, fontSize: isMobile ? '1.5rem' : '2.125rem' }}>+</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>Global Events</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Suspense fallback={<Box sx={{ width: '100px', height: '48px' }} />}>
                      <AnimatedCounter
                        target={200}
                        fontSize={isMobile ? 24 : 34}
                        padding={0}
                        places={[100, 10, 1]}
                        gap={2}
                        textColor="#ffd700"
                        fontWeight="bold"
                        gradientFrom="transparent"
                        gradientTo="transparent"
                      />
                    </Suspense>
                    <Typography variant="h4" sx={{ color: '#ffd700', fontWeight: 'bold', ml: 0.5, fontSize: isMobile ? '1.5rem' : '2.125rem' }}>+</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>Global Cities</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Suspense fallback={<Box sx={{ width: '100px', height: '48px' }} />}>
                      <AnimatedCounter
                        target={30}
                        fontSize={isMobile ? 24 : 34}
                        padding={0}
                        places={[10, 1]}
                        gap={2}
                        textColor="#ffd700"
                        fontWeight="bold"
                        gradientFrom="transparent"
                        gradientTo="transparent"
                      />
                    </Suspense>
                    <Typography variant="h4" sx={{ color: '#ffd700', fontWeight: 'bold', ml: 0.5, fontSize: isMobile ? '1.5rem' : '2.125rem' }}>+</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>Countries</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    <Suspense fallback={<Box sx={{ width: '100px', height: '48px' }} />}>
                      <AnimatedCounter
                        target={20}
                        fontSize={isMobile ? 24 : 34}
                        padding={0}
                        places={[10, 1]}
                        gap={2}
                        textColor="#ffd700"
                        fontWeight="bold"
                        gradientFrom="transparent"
                        gradientTo="transparent"
                      />
                    </Suspense>
                    <Typography variant="h4" sx={{ color: '#ffd700', fontWeight: 'bold', ml: 0.5, fontSize: isMobile ? '1.5rem' : '2.125rem' }}></Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>Musicians</Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box
              sx={{
                width: '100%',
                height: { xs: '500px', md: '600px' },
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(255,255,255,0.1)',
                overflow: 'hidden',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                position: 'relative'
              }}
            >
              {worldLocations && worldLocations.length > 0 && isClient && (
                <Suspense fallback={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress sx={{ color: '#ffd700' }} /></Box>}>
                  <InfiniteMenuFull items={worldLocations} />
                </Suspense>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Portfolio Section Component - Carousel
const PortfolioSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const portfolioItems = [
    {
      image: imperial1,
      title: "Aladdin the Movie",
      alt: "Imperial Drummers performing dhol alongside Will Smith on the set of Disney's Aladdin movie",
      description: "Performing alongside the living legend himself Will Smith in his new Disney movie Aladdin."
    },
    {
      image: imperial2,
      title: "Disneyland Paris",
      alt: "Imperial Drummers dhol players performing at the first ever wedding held at Disneyland Paris",
      description: "Taking the roof off the building at the first ever wedding in Disneyland Paris"
    },
    {
      image: imperial4,
      title: "Wedding Reception",
      alt: "Imperial Drummers dhol players energising a Punjabi wedding reception in London",
      description: "Taking the roof off the building at a Punjabi wedding in London"
    },
    {
      image: imperial5,
      title: "Meta Corporate Event",
      alt: "Imperial Drummers performing dhol at Meta corporate event in London with Bhangra legend Channi Singh",
      description: "Performing at the Meta Corporate Event in London alongside the Bhangra Legend Channi Singh"
    },
    {
      image: imperial6,
      title: "Only Ways Essex",
      alt: "Imperial Drummers performing dhol at a private party for the cast of The Only Way Is Essex",
      description: "Performing at a private party for the cast of The Only Way Is Essex"
    },
    {
      image: imperial7,
      title: "BBC Asian Network",
      alt: "Imperial Drummers performing live dhol at BBC Asian Network Certified event",
      description: "Shaking the ground at BBC Asian Network Certified"
    },
    {
      image: imperial8,
      title: "Meridian Grand Anniversary",
      alt: "Imperial Drummers celebrating 10 years of dhol performances at the Meridian Grand venue",
      description: "Celebrating 10 years of excellence at the Meridian Grand"
    },
  ];

  const visibleCount = isMobile ? 1 : isTablet ? 2 : 3;
  const maxIndex = Math.max(0, portfolioItems.length - visibleCount);
  const safeIndex = Math.min(currentIndex, maxIndex);

  const paginate = (dir) => {
    setCurrentIndex(prev => {
      const next = prev + dir;
      if (next < 0) return maxIndex;
      if (next > maxIndex) return 0;
      return next;
    });
  };

  return (
    <Box id="portfolio-section" sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" gutterBottom sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
          Our Portfolio
        </Typography>
        <Typography variant="h6" align="center" sx={{ mb: 6, color: '#666', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
          Recent performances and celebrations worldwide
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <Box sx={{ overflow: 'hidden', mx: { xs: 0, md: 2 } }}>
            <motion.div
              animate={{ x: `-${safeIndex * (100 / visibleCount)}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(e, { offset }) => {
                if (offset.x < -50) paginate(1);
                else if (offset.x > 50) paginate(-1);
              }}
              style={{ display: 'flex', cursor: 'grab' }}
            >
              {portfolioItems.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: `0 0 ${100 / visibleCount}%`,
                    maxWidth: `${100 / visibleCount}%`,
                    px: 1.5
                  }}
                >
                  <Card sx={{
                    height: '100%',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.03)' }
                  }}>
                    <CardMedia
                      component="img"
                      height="250"
                      image={item.image}
                      alt={item.alt}
                      loading="lazy"
                    />
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#5C005D' }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </motion.div>
          </Box>

          {/* Navigation Arrows */}
          <IconButton
            onClick={() => paginate(-1)}
            aria-label="Previous"
            sx={{
              position: 'absolute',
              left: { xs: 4, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(92, 0, 93, 0.8)',
              color: 'white',
              '&:hover': { backgroundColor: '#5C005D' },
              zIndex: 2,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => paginate(1)}
            aria-label="Next"
            sx={{
              position: 'absolute',
              right: { xs: 4, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(92, 0, 93, 0.8)',
              color: 'white',
              '&:hover': { backgroundColor: '#5C005D' },
              zIndex: 2,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* Dot Indicators */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <Box
              key={i}
              onClick={() => setCurrentIndex(i)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: i === safeIndex ? '#5C005D' : 'rgba(92, 0, 93, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { backgroundColor: i === safeIndex ? '#5C005D' : 'rgba(92, 0, 93, 0.5)' }
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

// Why Choose Section Component
// eslint-disable-next-line no-unused-vars
const WhyChooseSection = () => (
  <Box sx={{
    py: 8,
    background: 'linear-gradient(135deg, #1a0033 0%, #2d0052 50%, #1a0033 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 50%, rgba(160, 137, 9, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(92, 0, 93, 0.2) 0%, transparent 50%)',
      pointerEvents: 'none'
    }
  }}>
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        variant="h2"
        align="center"
        gutterBottom
        sx={{
          mb: 2,
          color: '#ffd700',
          fontWeight: 'bold',
          textShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
          fontSize: { xs: '2.5rem', md: '3.75rem' }
        }}
      >
        Why Choose Imperial Drummers?
      </Typography>
      <Typography
        variant="h6"
        align="center"
        sx={{
          mb: 6,
          color: 'rgba(255, 255, 255, 0.9)',
          maxWidth: '800px',
          mx: 'auto',
          fontSize: { xs: '1.125rem', md: '1.25rem' }
        }}
      >
        What makes us the world's premier international dhol entertainment service
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(160, 137, 9, 0.3)',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: '1px solid rgba(255, 215, 0, 0.6)',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <CheckIcon sx={{
                fontSize: 56,
                mb: 2,
                color: '#ffd700',
                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))'
              }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                Authentic Performances
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                Traditional Punjabi dhol rhythms and Bollywood entertainment performed by experienced musicians
                who understand the cultural significance of each celebration.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(160, 137, 9, 0.3)',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: '1px solid rgba(255, 215, 0, 0.6)',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <LocationIcon sx={{
                fontSize: 56,
                mb: 2,
                color: '#ffd700',
                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))'
              }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                Worldwide Travel
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                Our UK-based dhol players travel to Indian weddings and Asian cultural events
                across 30+ countries, handling all international logistics seamlessly.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(160, 137, 9, 0.3)',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: '1px solid rgba(255, 215, 0, 0.6)',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <StarIcon sx={{
                fontSize: 56,
                mb: 2,
                color: '#ffd700',
                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))'
              }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                Professional Quality
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                Award-winning musicians with professional-grade equipment and backup performers
                to ensure your celebration goes perfectly.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(160, 137, 9, 0.3)',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: '1px solid rgba(255, 215, 0, 0.6)',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <MusicIcon sx={{
                fontSize: 56,
                mb: 2,
                color: '#ffd700',
                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))'
              }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                Custom Performances
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                Tailored performances based on your specific requirements, regional preferences,
                and cultural traditions.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(160, 137, 9, 0.3)',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: '1px solid rgba(255, 215, 0, 0.6)',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <GroupIcon sx={{
                fontSize: 56,
                mb: 2,
                color: '#ffd700',
                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))'
              }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                Flexible Team Size
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                From solo performances to full ensembles - we scale our team based on your
                event size and budget requirements.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.4) 0%, rgba(45, 0, 82, 0.6) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(160, 137, 9, 0.3)',
            transition: 'all 0.4s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              border: '1px solid rgba(255, 215, 0, 0.6)',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.2)',
              background: 'linear-gradient(145deg, rgba(92, 0, 93, 0.6) 0%, rgba(45, 0, 82, 0.8) 100%)'
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <EventIcon sx={{
                fontSize: 56,
                mb: 2,
                color: '#ffd700',
                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))'
              }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                Event Coordination
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.7 }}>
                Perfect timing coordination with your wedding planners, photographers, and venue
                staff for seamless celebrations.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// Luxury Showcase Section Component
const LuxuryShowcaseSection = () => {
  const isClient = useIsClient();
  return (
    <Box id="luxury-section" sx={{
      position: 'relative',
      py: { xs: 6, md: 12 },
      backgroundColor: '#0a0015',
      overflow: 'hidden',
      minHeight: { xs: '100vh', md: '90vh' }
    }}>
      {/* Light Rays Background */}
      {isClient && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}>
          <Suspense fallback={<div />}>
            <LightRays
              raysOrigin="top-center"
              raysColor="#ffd700"
              raysSpeed={1}
              lightSpread={0.3}
              rayLength={5}
              pulsating={true}
              fadeDistance={3}
              saturation={2}
              followMouse={true}
              mouseInfluence={0.3}
              noiseAmount={0}
              distortion={0}
            />
          </Suspense>
        </div>
      )}

      {/* Dark overlay for text contrast */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: {
          xs: 'linear-gradient(180deg, rgba(10,0,21,0.1) 0%, rgba(10,0,21,0.25) 50%, rgba(10,0,21,0.35) 100%)',
          md: 'linear-gradient(180deg, rgba(10,0,21,0.15) 0%, rgba(10,0,21,0.3) 50%, rgba(10,0,21,0.4) 100%)'
        },
        zIndex: 1
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              color: '#ffd700',
              fontWeight: 'bold',
              textShadow: '0 0 40px rgba(255, 215, 0, 0.8), 2px 2px 8px rgba(0,0,0,0.8)',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.75rem' }
            }}
          >
            Luxury Package
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              mb: 3,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              textShadow: '2px 2px 6px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.6)',
              fontSize: { xs: '1.125rem', md: '1.5rem' }
            }}
          >
            Experience the grandeur of our premium, handcrafted ceremonial uniforms that define luxury and tradition
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  maxWidth: '300px',
                  width: '100%',
                  border: '3px solid rgba(212, 175, 55, 0.5)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(212, 175, 55, 0.4)',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 30px 80px rgba(212, 175, 55, 0.6)',
                    border: '3px solid rgba(212, 175, 55, 0.8)'
                  }
                }}
              >
                <Box
                  component="img"
                  src={luxuryOutfits}
                  alt="Luxury Imperial Drummers Marching Band Outfits"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{
              p: { xs: 4, md: 6 },
              backgroundColor: 'rgba(20, 5, 40, 0.85)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              border: '2px solid rgba(212, 175, 55, 0.4)',
              boxShadow: '0 8px 32px rgba(212, 175, 55, 0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  color: '#d4af37',
                  fontWeight: 'bold',
                  mb: 3,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Elevate Your Event with Luxury
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                  lineHeight: 1.8,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Our performers wear only the finest ceremonial attire, ensuring your event exudes sophistication
                and grandeur. Each outfit is a masterpiece, combining centuries of tradition with contemporary luxury.
              </Typography>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  size="large"
                  color="gold"
                  sx={{
                    px: 6,
                    py: 2,
                    boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)',
                    '&:hover': {
                      boxShadow: '0 12px 25px rgba(255, 215, 0, 0.4)',
                    }
                  }}
                  onClick={scrollToBooking}
                >
                  Book a Luxury Performance
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// // Testimonials Section Component
// const TestimonialsSection = () => (
//   <Box id="testimonials-section" sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
//     <Container maxWidth="lg">
//       <Typography variant="h2" align="center" gutterBottom sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
//         What Our Clients Say
//       </Typography>
//       <Typography variant="h6" align="center" sx={{ mb: 6, color: '#666', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
//         Reviews from international celebrations worldwide
//       </Typography>

//       <Grid container spacing={4}>
//         <Grid item xs={12} md={4}>
//           <Paper sx={{ p: 4, height: '100%', position: 'relative' }}>
//             <Box sx={{ display: 'flex', mb: 2 }}>
//               <Rating value={5} readOnly size="small" />
//             </Box>
//             <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
//               "Flying Imperial Drummers to our New York wedding was the best decision we made! They coordinated everything
//               perfectly across time zones and made our Manhattan celebration unforgettable for guests from 8 countries."
//             </Typography>
//             <Box sx={{ display: 'flex', alignItems: 'center' }}>
//               <Avatar sx={{ mr: 2, bgcolor: '#5C005D' }}>A</Avatar>
//               <Box>
//                 <Typography variant="h6">Anita & Michael</Typography>
//                 <Typography variant="body2" color="text.secondary">New York, USA - October 2023</Typography>
//               </Box>
//             </Box>
//           </Paper>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <Paper sx={{ p: 4, height: '100%', position: 'relative' }}>
//             <Box sx={{ display: 'flex', mb: 2 }}>
//               <Rating value={5} readOnly size="small" />
//             </Box>
//             <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
//               "Having Imperial Drummers at our Dubai corporate gala was spectacular! They brought authentic energy to our
//               international client event and received standing ovations. Worth every penny for the global impact."
//             </Typography>
//             <Box sx={{ display: 'flex', alignItems: 'center' }}>
//               <Avatar sx={{ mr: 2, bgcolor: '#5C005D' }}>R</Avatar>
//               <Box>
//                 <Typography variant="h6">Raj Enterprises</Typography>
//                 <Typography variant="body2" color="text.secondary">Dubai, UAE - November 2023</Typography>
//               </Box>
//             </Box>
//           </Paper>
//         </Grid>

//         <Grid item xs={12} md={4}>
//           <Paper sx={{ p: 4, height: '100%', position: 'relative' }}>
//             <Box sx={{ display: 'flex', mb: 2 }}>
//               <Rating value={5} readOnly size="small" />
//             </Box>
//             <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
//               "Booked them for our corporate Diwali celebration. They understood exactly what we needed
//               and delivered an authentic performance that had everyone dancing!"
//             </Typography>
//             <Box sx={{ display: 'flex', alignItems: 'center' }}>
//               <Avatar sx={{ mr: 2, bgcolor: '#5C005D' }}>M</Avatar>
//               <Box>
//                 <Typography variant="h6">Manchester Corp Events</Typography>
//                 <Typography variant="body2" color="text.secondary">Corporate Event, November 2023</Typography>
//               </Box>
//             </Box>
//           </Paper>
//         </Grid>
//       </Grid>
//     </Container>
//   </Box>
// );

// Global Service Areas Section Component
// eslint-disable-next-line no-unused-vars
const ServiceAreasSection = () => (
  <Box id="service-areas-section" sx={{ py: 8, backgroundColor: 'white' }}>
    <Container maxWidth="lg">
      <Typography variant="h2" align="center" gutterBottom sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
        Global Coverage Across 30+ Countries
      </Typography>
      <Typography variant="h6" align="center" sx={{ mb: 6, color: '#666', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
        Professional dhol entertainment available worldwide with local expertise. We regularly expand our global network and travel to new destinations for special events.
        <br />
        Contact us to discuss bringing Imperial Drummers to you.
      </Typography>
    </Container>
  </Box>
);

// How It Works Section Component
// eslint-disable-next-line no-unused-vars
const HowItWorksSection = () => (
  <Box sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
    <Container maxWidth="lg">
      <Typography variant="h2" align="center" gutterBottom sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
        How It Works
      </Typography>
      <Typography variant="h6" align="center" sx={{ mb: 6, color: '#666', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
        Simple booking process for your perfect celebration
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#5C005D',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              1
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#5C005D' }}>Get Quote</Typography>
            <Typography variant="body1" color="text.secondary">
              Fill out our simple form with your event details and receive a personalised quote within 24 hours.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#5C005D',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              2
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#5C005D' }}>Book & Confirm</Typography>
            <Typography variant="body1" color="text.secondary">
              Review your quote, make any adjustments, and secure your date with a simple booking deposit.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#5C005D',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              3
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#5C005D' }}>Coordinate</Typography>
            <Typography variant="body1" color="text.secondary">
              Our team coordinates with your event planner and venue to ensure perfect timing and setup.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#5C005D',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              fontSize: '2rem',
              fontWeight: 'bold'
            }}>
              4
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#5C005D' }}>Celebrate</Typography>
            <Typography variant="body1" color="text.secondary">
              Sit back and enjoy as our professional musicians create magical moments for your special day.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// FAQ Section Component
const FAQSection = () => {
  const faqs = [
    {
      question: "How many dhol players do I need for my wedding?",
      answer: "Most weddings use 2 dhol players, which provides a strong balance of sound and visual impact. For larger venues or grand celebrations, 3-5 players create an immersive experience. A solo dhol player works well for intimate ceremonies or as an addition to the baraat procession."
    },
    {
      question: "Where can I hire dhol players in the UK?",
      answer: "Imperial Drummers provides professional dhol players across every major city in the UK, including London, Birmingham, Manchester, Leicester, Leeds, and over 100 other locations nationwide. We also travel internationally to 30+ countries for destination weddings and events."
    },
    {
      question: "How far in advance should I book dhol players?",
      answer: "We recommend booking at least 3-6 months in advance, especially during peak wedding season (April-September). Popular dates fill up quickly, so early enquiry is best to secure your preferred date. For international events, 6+ months advance booking allows time for travel arrangements and coordination."
    },
    {
      question: "What types of events do dhol players perform at?",
      answer: "Dhol players perform at a wide range of events including Asian weddings (baraat, milni, reception), corporate events (product launches, Diwali celebrations, awards ceremonies), cultural festivals (melas, Vaisakhi parades, Navratri), and media productions (music videos, film, TV, radio)."
    },
    {
      question: "Do you handle international travel and logistics?",
      answer: "Yes, Imperial Drummers manages all international travel arrangements including flights, equipment transportation, customs clearance, and local logistics. We have performed in over 30 countries across Europe, North America, Asia, the Middle East, and beyond."
    },
    {
      question: "What is included when you book dhol players?",
      answer: "Our packages include professional dhol players, authentic traditional or luxury costumes, premium dhol instruments, sound equipment if required, full coordination with your venue and event planners, and flexible performance durations. We tailor every booking to your specific event requirements."
    },
    {
      question: "Can dhol players perform alongside a DJ or live band?",
      answer: "Yes, our dhol players regularly perform alongside DJs, live bands, and MCs. Our drummers are experienced in reading the flow of an event and coordinating with other entertainment to create a seamless, high-energy experience throughout your celebration."
    }
  ];

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>
      <Box id="faq-section" sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" align="center" sx={{ mb: 6, color: '#666', fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
            Everything you need to know about hiring dhol players
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ color: '#5C005D' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

// Contact CTA Section Component
// eslint-disable-next-line no-unused-vars
const ContactCTASection = () => {
  const isClient = useIsClient();
  return (
    <Box sx={{
      position: 'relative',
      py: { xs: 10, md: 16 },
      backgroundColor: '#1a0a2e',
      overflow: 'hidden',
      minHeight: '60vh'
    }}>
      {/* Liquid Ether Background */}
      {isClient && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}>
          <Suspense fallback={<div />}>
            <LiquidEther
              colors={['#8017b5', '#8f4d8c', '#a12b9f']}
              mouseForce={20}
              cursorSize={100}
              isViscous={false}
              viscous={50}
              iterationsViscous={32}
              iterationsPoisson={32}
              resolution={0.5}
              isBounce={false}
              autoDemo={true}
              autoSpeed={0.8}
              autoIntensity={2.2}
              takeoverDuration={0.25}
              autoResumeDelay={3000}
              autoRampDuration={0.6}
            />
          </Suspense>
        </div>
      )}

      {/* Dark overlay for text contrast */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 1
      }} />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              color: 'white',
              mb: 4,
              fontWeight: 'bold',
              fontSize: { xs: '2.5rem', md: '3.75rem' }
            }}
          >
            Global Coverage Across 30+ Countries
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              mb: 3,
              maxWidth: '850px',
              mx: 'auto',
              lineHeight: 1.8,
              fontSize: { xs: '1.125rem', md: '1.5rem' }
            }}
          >
            Professional dhol entertainment available worldwide with local expertise. We regularly expand our global network and travel to new destinations for special events.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              maxWidth: '700px',
              mx: 'auto',
              fontStyle: 'italic',
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}
          >
            Contact us to discuss bringing Imperial Drummers to you.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;
